<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.crm.leadsBySource') }}</h5>
      </div>
      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block rounded-md border border-gray-100 py-[5px] md:py-[6.5px] px-[12px] md:px-[19px] transition-all hover:bg-gray-50 dark:border-[#172036] dark:hover:bg-[#0a0e19]': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              {{ directT(`dashboard.${selectedTimeFilterKey}`) }}
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>
          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[5] dark:bg-dark dark:shadow-none"
            v-if="isActive"
          >
            <li v-for="filter in timeFilters" :key="filter.key">
              <button
                type="button"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
                @click="applyTimeFilter(filter.key)"
              >
                {{ directT(`dashboard.${filter.key}`) }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="trezo-card-content">
      <div class="-mt-[10px]" v-if="!loading && sourceData.length > 0" style="height: 400px;">
        <apexchart
          v-if="isClient"
          type="donut"
          height="350"
          :options="leadsBySource"
          :series="source"
        ></apexchart>
      </div>
      <div v-else-if="loading" class="flex justify-center items-center h-[350px]">
        <span class="text-gray-500">{{ directT('dashboard.crm.loadingData') }}</span>
      </div>
      <div v-else class="flex justify-center items-center h-[350px]">
        <div class="text-center">
          <i class="ri-information-line text-4xl text-gray-300 mb-2 block"></i>
          <span class="text-gray-500 block">{{ directT('dashboard.crm.noLeadsFound') }}</span>
          <span class="text-gray-400 text-sm block mt-2">{{ directT('dashboard.crm.noLeadsForPeriod', { period: directT(`dashboard.${selectedTimeFilterKey}`) }) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useSupabaseClient } from '@/composables/useSupabaseClient';
import { getCurrentOrgId } from '@/utils/getCurrentOrgId';
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define types for the data
interface LeadSource {
  source: unknown;
}

interface TimeFilter {
  key: string;
  translationKey: string;
}

export default defineComponent({
  name: "LeadsBySourceChart",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isClient = ref(false);
    const loading = ref(true);
    const sourceData = ref<{ source: string, count: number }[]>([]);
    const { supabase } = useSupabaseClient();
    const currentOrgId = ref<string | null>(null);
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params?: Record<string, string>): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      // Handle parameter substitution
      let translatedText = typeof result === 'string' ? result : key;
      
      if (params && typeof translatedText === 'string') {
        Object.entries(params).forEach(([paramName, paramValue]) => {
          translatedText = translatedText.replace(`{${paramName}}`, paramValue);
        });
      }
      
      return translatedText;
    };

    // Define time filters with translation keys
    const timeFilters = [
      { key: 'thisDay', translationKey: 'dashboard.thisDay' },
      { key: 'thisWeek', translationKey: 'dashboard.thisWeek' },
      { key: 'thisMonth', translationKey: 'dashboard.thisMonth' },
      { key: 'thisYear', translationKey: 'dashboard.thisYear' }
    ];
    
    const selectedTimeFilterKey = ref('thisMonth');
    
    const colors = [
      "#605DFF", // primary-500
      "#3584FC", // secondary-500
      "#AD63F6", // purple-500
      "#0dcaf0", // info-500
      "#25B003", // success-500
      "#FD5812", // danger-500
    ];

    const source = computed(() => {
      return sourceData.value.map(item => item.count);
    });

    const sourceLabels = computed(() => {
      return sourceData.value.map(item => item.source);
    });

    // Apply time filter with translation key
    const applyTimeFilter = (filterKey: string) => {
      selectedTimeFilterKey.value = filterKey;
      toggleActive();
      fetchLeadSources(); // Reload data with new filter
    };

    // Calculate date limits based on current filter
    const getDateFilter = () => {
      const now = new Date();
      let startDate;
      
      switch (selectedTimeFilterKey.value) {
        case "thisDay":
          // Today (from beginning of day)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
          
        case "thisWeek": {
          // From beginning of current week (Sunday)
          const dayOfWeek = now.getDay(); // 0-6 (0 = Sunday)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
          break;
        }
          
        case "thisMonth":
          // From beginning of current month
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
          
        case "thisYear":
          // From beginning of current year
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
          
        default:
          // Default - beginning of month (useful in case of error)
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
      }
      
      // Convert to ISO format for query
      return startDate.toISOString();
    };

    const fetchCurrentOrgId = async () => {
      currentOrgId.value = await getCurrentOrgId();
      if (!currentOrgId.value) {
        console.error('Failed to get current organization ID');
      }
      return currentOrgId.value;
    };

    const fetchLeadSources = async () => {
      loading.value = true;
      try {
        // Get current organization ID first
        const orgId = currentOrgId.value || await fetchCurrentOrgId();
        
        if (!orgId) {
          console.error('No organization ID available, cannot fetch leads');
          return;
        }

        // Calculate start date for filtering
        const startDate = getDateFilter();

        // Fetch leads filtered by the current user's organization and date filter
        const { data, error } = await supabase
          .from('leads')
          .select('source')
          .eq('org_id', orgId)
          .gte('created_at', startDate) // Filter by date
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching lead sources:', error);
          return;
        }

        // Count leads by source
        const sourceCounts: Record<string, number> = {};
        
        // Make sure data exists before using forEach
        if (data) {
          data.forEach((lead: LeadSource) => {
            if (lead.source && typeof lead.source === 'string') {
              sourceCounts[lead.source] = (sourceCounts[lead.source] || 0) + 1;
            }
          });
        }

        // Convert to array of objects
        sourceData.value = Object.entries(sourceCounts).map(([source, count]) => ({
          source,
          count
        }));
      } catch (error) {
        console.error('Failed to fetch lead sources:', error);
      } finally {
        loading.value = false;
      }
    };

    const leadsBySource = computed(() => {
      return {
        chart: {
          height: 350,
          type: "donut",
        },
        labels: sourceLabels.value,
        colors: colors,
        stroke: {
          width: 1,
          show: true,
          colors: ["#ffffff"],
        },
        legend: {
          show: true,
          position: "bottom",
          fontSize: "12px",
          horizontalAlign: "center",
          itemMargin: {
            horizontal: 8,
            vertical: 7,
          },
          labels: {
            colors: "#64748B",
          },
          markers: {
            width: 9,
            height: 9,
            offsetX: -2,
            offsetY: -0.5,
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              labels: {
                show: true,
                name: {
                  color: "#64748B",
                },
                value: {
                  show: true,
                  color: "#3A4252",
                  fontSize: "28px",
                  fontWeight: "600",
                },
                total: {
                  show: true,
                  color: "#64748B",
                  label: directT('dashboard.crm.totalLeads'),
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
        // Fixed overall chart size
        responsive: [{
          breakpoint: 9999,
          options: {
            chart: {
              height: 350
            }
          }
        }]
      };
    });

    onMounted(() => {
      isClient.value = true;
      fetchLeadSources();
    });

    return {
      isClient,
      loading,
      source,
      sourceData,
      leadsBySource,
      isActive,
      toggleActive,
      colors,
      timeFilters,
      selectedTimeFilterKey,
      applyTimeFilter,
      directT
    };
  },
});
</script>
